import { Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { FormikProvider, useFormik } from 'formik';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';
import * as yup from 'yup';

import {
  Address,
  FloatNumberFieldFormik,
  PhoneInputFormik,
  TaxIDInputFormik,
  TextField,
} from '@woovi/form';
import { useMutationCallbacks } from '@woovi/relay';
import { CHECKOUT_FIELD_TYPE_ENUM } from '@woovi/shared';
import { ActionButton } from '@woovi/ui';
import { convertToMoney } from '@woovi/utils';

import type { PaymentCheckoutPixAutomaticForm_checkout$key } from './__generated__/PaymentCheckoutPixAutomaticForm_checkout.graphql.ts';
import { CheckoutPixAutomaticAdd } from './mutations/CheckoutPixAutomaticAddMutation.tsx';

type PaymentCheckoutPixAutomaticProps = {
  checkout: PaymentCheckoutPixAutomaticForm_checkout$key;
};

type CheckoutPixAutomaticForm = {
  name: string;
  taxID: string;
  value?: number;
  email?: string;
  phone?: string;
  additionalInfo: {
    [key: string]: string | undefined;
  };
};

const PaymentCheckoutPixAutomaticForm = (
  props: PaymentCheckoutPixAutomaticProps,
): ReactNode => {
  const { t } = useTranslation();

  const checkout = useFragment<PaymentCheckoutPixAutomaticForm_checkout$key>(
    graphql`
      fragment PaymentCheckoutPixAutomaticForm_checkout on Checkout {
        id
        dynamicValue
        minimumValue
        customFields {
          title
          type
          required
        }
      }
    `,
    props.checkout,
  );

  const [checkoutPixAutomaticAdd, isPending] = useMutationCallbacks({
    name: 'CheckoutPixAutomaticAdd',
    mutation: CheckoutPixAutomaticAdd,
    afterCompleted: (response) => {
      const paymentSubscription =
        response?.CheckoutPixAutomaticAdd?.paymentSubscription;

      if (paymentSubscription?.pixRecurring?.paymentLinkUrl) {
        window.open(
          paymentSubscription.pixRecurring.paymentLinkUrl,
          '_self',
          'noopener noreferrer',
        );
      }
    },
  });

  const onSubmit = (values: CheckoutPixAutomaticForm): void => {
    checkoutPixAutomaticAdd({
      variables: {
        input: {
          checkout: checkout.id,
          value: values.value,
          customer: {
            name: values.name,
            taxID: values.taxID,
            email: values.email,
            phone: values.phone,
            address: values.address,
          },
          additionalInfo: Object.keys(values.additionalInfo || {}).map(
            (field) => ({
              key: field,
              value: values.additionalInfo[field] || '',
            }),
          ),
        },
      },
    });
  };

  const getAdditionalFieldSchema = () => {
    if (!checkout.customFields) {
      return {};
    }

    const obj: any = {};

    checkout.customFields
      .filter((field) => field?.type === CHECKOUT_FIELD_TYPE_ENUM.TEXT)
      .forEach((field) => {
        if (field?.required) {
          obj[field?.title as string] = yup.string().required(t('Required'));
        } else {
          obj[field?.title as string] = yup.string();
        }
      });

    return obj;
  };

  const getCustomerPhoneSchema = (): { phone?: yup.AnySchema } => {
    const phoneField = checkout.customFields?.find(
      (field) => field?.type === CHECKOUT_FIELD_TYPE_ENUM.PHONE,
    );

    if (!phoneField) {
      return {};
    }

    if (!phoneField.required) {
      return {
        phone: yup.string().isValidPhoneNumber(t('Phone is invalid')),
      };
    }

    return {
      phone: yup
        .string()
        .isValidPhoneNumber(t('Phone is invalid'))
        .required(t('Required')),
    };
  };

  const getCustomerAddressSchema = (): { address?: yup.AnySchema } => {
    const addressField = checkout.customFields?.find(
      (field) => field?.type === CHECKOUT_FIELD_TYPE_ENUM.ADDRESS,
    );

    if (!addressField) {
      return {};
    }

    if (!addressField.required) {
      return {
        address: yup.object({
          zipcode: yup.string(),
          street: yup.string(),
          neighborhood: yup.string(),
          city: yup.string(),
          state: yup.string(),
          number: yup.string(),
          complement: yup.string(),
        }),
      };
    }

    return {
      address: yup.object({
        zipcode: yup.string().required(t('Required')),
        street: yup.string().required(t('Required')),
        neighborhood: yup.string().required(t('Required')),
        city: yup.string().required(t('Required')),
        state: yup.string().required(t('Required')),
        number: yup.string().required(t('Required')),
        complement: yup.string(),
      }),
    };
  };

  const getCustomerEmailSchema = (): { email?: yup.AnySchema } => {
    const emailField = checkout.customFields?.find(
      (field) => field?.type === CHECKOUT_FIELD_TYPE_ENUM.EMAIL,
    );

    if (!emailField) {
      return {};
    }

    if (!emailField.required) {
      return {
        email: yup.string().email(t('Invalid email')),
      };
    }

    return {
      email: yup.string().email(t('Invalid email')).required(t('Required')),
    };
  };

  const formikbag = useFormik<CheckoutPixAutomaticForm>({
    initialValues: {
      name: '',
      taxID: '',
    },
    validationSchema: yup.object().shape({
      value: yup.number().nullable(),
      name: yup.string().required(t('Name is required')),
      taxID: yup.string().required(t('TaxID is required')),
      additionalInfo: yup.object({
        ...getAdditionalFieldSchema(),
      }),
      ...getCustomerPhoneSchema(),
      ...getCustomerEmailSchema(),
      ...getCustomerAddressSchema(),
    }),
    onSubmit,
  });

  return (
    <FormikProvider value={formikbag}>
      <Stack>
        <TextField name='name' label={t('Name')} placeholder={t('Name')} />
        {/* <TextField name='email' placeholder={t('Email')} />
            <PhoneInputFormik name='phone' placeholder={t('Phone')} /> */}
        <TaxIDInputFormik
          name='taxID'
          label={t('CPF or CNPJ')}
          placeholder={t('CPF or CNPJ')}
          mt='10px'
        />

        {checkout.dynamicValue && (
          <>
            <FloatNumberFieldFormik
              name='value'
              label={t('Value')}
              placeholder={t('Value')}
              fullWidth
            />
            <Typography>
              {t('The minimum value is {{minimumValue}}', {
                minimumValue: convertToMoney(checkout.minimumValue || 0),
              })}
            </Typography>
          </>
        )}
        {checkout.customFields?.map((field) => {
          if (field?.type === CHECKOUT_FIELD_TYPE_ENUM.PHONE) {
            return (
              <PhoneInputFormik
                name='phone'
                label={field.title}
                placeholder={field.title}
                required={!!field.required}
              />
            );
          }

          if (field?.type === CHECKOUT_FIELD_TYPE_ENUM.EMAIL) {
            return (
              <TextField
                type='email'
                name='email'
                label={field.title}
                placeholder={field.title}
                required={!!field.required}
              />
            );
          }

          if (field?.type === CHECKOUT_FIELD_TYPE_ENUM.ADDRESS) {
            return <Address prefix='address' showHeader={false} />;
          }

          if (field?.type === CHECKOUT_FIELD_TYPE_ENUM.TEXT) {
            return (
              <TextField
                name={`additionalInfo.${field.title}`}
                placeholder={field.title}
                label={field.title}
                required={!!field.required}
              />
            );
          }

          return null;
        })}
        <ActionButton
          disabled={!formikbag.isValid}
          loading={isPending}
          onClick={formikbag.handleSubmit}
          sx={{
            marginTop: 2,
          }}
        >
          {t('Create QR Code')}
        </ActionButton>
      </Stack>
    </FormikProvider>
  );
};

export default PaymentCheckoutPixAutomaticForm;
